import { useCallback } from "react";
import { OrbitControls } from "three-stdlib";
import { gsapFactory, type GSAPTweenLike } from "../../core/gsapFactory";
import { EaseOptions, SharedOpts } from "../../core/index.types";

/**
 * OrbitControls use infinite numbers on MinAzimuthAngle, MaxAzimuthAngle, MaxDistance & MaxZoom.
 * GSAP cannot interpolate Infinity, so values must be sanitized before animation.
 *
 * Three-part defense to prevent orbitControls from being mutated at tween creation time:
 *
 * 1. PROXY — a plain object is animated instead of orbitControls directly. GSAP writes "from"
 *    values to the proxy, never to orbitControls. onUpdate copies proxy → orbitControls only
 *    during playback.
 *
 * 2. immediateRender: false — tells GSAP not to render at time=0 on tween creation. Without this,
 *    GSAP renders the "from" state immediately even for paused tweens, which would fire onUpdate
 *    and apply the current angle to orbitControls, locking the camera.
 *
 * 3. hasStarted guard — because GSAP sometimes ignores immediateRender: false (e.g. when
 *    duration/ease were mixed into fromVars, or when added to a timeline), onUpdate is guarded
 *    by a flag that is only set true in onStart. onStart only fires on actual .play(), never
 *    at the creation-time render.
 *
 * @see https://threejs.org/docs/#OrbitControls
 */

export type AngleOpts = SharedOpts & {
  ease?: EaseOptions;
  duration: number;
  minAzimuth: number;
  maxAzimuth: number;
  minPolar: number;
  maxPolar: number;
};

export type PositionOpts = SharedOpts & {
  ease?: EaseOptions;
  position: number;
  keyframes?: number[];
};

const wrapToPi = (a: number) => (((a + Math.PI) % (2 * Math.PI)) + (2 * Math.PI)) % (2 * Math.PI) - Math.PI;

const getPolarSafe = (ob: OrbitControls) => {
  const currentPolar = Number(ob.getPolarAngle());
  // currentPolar = wrapToPi(currentPolar) // normalized
  if (!Number.isFinite(currentPolar)) return { min: -Math.PI, max: Math.PI };
  return { min: currentPolar, max: currentPolar };
};

const getAzimuthSafe = (ob: OrbitControls) => {
  const currentAzimuth = Number(ob.getAzimuthalAngle());
  // currentAzimuth = wrapToPi(currentAzimuth) // normalized
  if (!Number.isFinite(currentAzimuth)) return { min: -Math.PI, max: Math.PI };
  return { min: currentAzimuth, max: currentAzimuth };
};

export function useGsapOrbitAngles() {
  return useCallback((orbitControls: OrbitControls, opts: AngleOpts, id?: string) => {
    if (!orbitControls) throw new Error("Missing OrbitControls");

    const { duration = 2, ease = EaseOptions.Power1InOut, minAzimuth, maxAzimuth, minPolar, maxPolar, repeat = 0, yoyo = false } = opts; //prettier-ignore

    const proxy = { minAzimuth: 0, maxAzimuth: 0, minPolar: 0, maxPolar: 0 };
    let hasStarted = false;

    const t = gsapFactory.fromTo(
      proxy,
      {
        immediateRender: false,
        minAzimuth: () => getAzimuthSafe(orbitControls).min, // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
        maxAzimuth: () => getAzimuthSafe(orbitControls).max, // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
        minPolar:   () => getPolarSafe(orbitControls).min,   // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
        maxPolar:   () => getPolarSafe(orbitControls).max,   // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
      },
      {
        duration,
        ease,
        minAzimuth,
        maxAzimuth,
        minPolar,
        maxPolar,
        repeat,
        yoyo,
        onStart: () => { hasStarted = true; },
        onUpdate: () => {
          if (!hasStarted) return;
          orbitControls.minAzimuthAngle = proxy.minAzimuth;
          orbitControls.maxAzimuthAngle = proxy.maxAzimuth;
          orbitControls.minPolarAngle   = proxy.minPolar;
          orbitControls.maxPolarAngle   = proxy.maxPolar;
          orbitControls.update();
        },
      },
      id
    );

    return t;
  }, []);
}

const getSafeDistance = (ob: OrbitControls) => {
  const minDistance = Number.isFinite(Number(ob.minDistance)) ? Number(ob.minDistance) : 0; //prettier-ignore
  const maxDistance = Number.isFinite(Number(ob.maxDistance)) ? Number(ob.maxDistance) : 10000000; //prettier-ignore
  const currentDistance = Number.isFinite(Number(ob.getDistance())) ? Number(ob.getDistance()) : Number.isFinite(minDistance) ? minDistance : Number.isFinite(maxDistance) ? maxDistance : 1; //prettier-ignore
  return { min: currentDistance, max: currentDistance };
};

export function useGsapOrbitPosition() {
  return useCallback((orbitControls: OrbitControls, opts: PositionOpts, id?: string): GSAPTweenLike => {
    if (!orbitControls) throw new Error("Missing OrbitControls");

    const { duration = 5, ease = EaseOptions.Power1In, position, repeat = 0, yoyo = false, keyframes } = opts; //prettier-ignore

    const proxy = { minDistance: 0, maxDistance: 0 };
    let hasStarted = false;

    // If keyframes provided, use keyframes syntax
    if (keyframes?.length) {
      const t = gsapFactory.fromTo(
        proxy,
        {
          immediateRender: false,
          minDistance: () => getSafeDistance(orbitControls).min, // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
          maxDistance: () => getSafeDistance(orbitControls).max, // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
        },
        {
          duration,
          ease,
          repeat,
          yoyo,
          keyframes: keyframes.map((pos) => ({
            minDistance: Number(pos),
            maxDistance: Number(pos),
          })),
          onStart: () => { hasStarted = true; },
          onUpdate: () => {
            if (!hasStarted) return;
            orbitControls.minDistance = proxy.minDistance;
            orbitControls.maxDistance = proxy.maxDistance;
            orbitControls.update();
          },
        },
        id
      );
      return t;
    }

    // Single position tween
    const t = gsapFactory.fromTo(
      proxy,
      {
        immediateRender: false,
        minDistance: () => getSafeDistance(orbitControls).min, // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
        maxDistance: () => getSafeDistance(orbitControls).max, // anonymous functions ensures gsap invalidates cache and does not use stale orbitcontrols values
      },
      {
        duration,
        ease,
        minDistance: Number(position),
        maxDistance: Number(position),
        repeat,
        yoyo,
        onStart: () => { hasStarted = true; },
        onUpdate: () => {
          if (!hasStarted) return;
          orbitControls.minDistance = proxy.minDistance;
          orbitControls.maxDistance = proxy.maxDistance;
          orbitControls.update();
        },
      },
      id
    );

    return t;
  }, []);
}
